/**
 * 高山市・計測エリアの短文コンテキスト（プロンプト注入用）
 * 内容は運用で検証・更新すること。
 */

export const TAKAYAMA_BASE_JA = [
  '岐阜県高山市は山間の観光都市で、古い町並・朝市など国内外からの観光客が多い。',
  '冬季は積雪の影響で歩行者・混雑の水準が下がりやすい。',
  '商店街によって「観光客が主」「地元利用が主」「混在」など客層が異なる。',
];

export const TAKAYAMA_BASE_EN = [
  'Takayama is a mountain tourism city in Gifu Prefecture with strong visitor demand around the old town and morning markets.',
  'Winter snow tends to reduce pedestrian counts and congestion levels.',
  'Shopping streets differ in whether demand is mainly tourists, locals, or mixed.',
];

/** @type {Record<string, string>} */
export const LOCATION_CONTEXT_JA = {
  'old-town': '近年はやや増加傾向。水曜は低め、土曜は高めで、イベント日は通常日より混みやすい。',
  station: '近年はやや増加傾向。火曜は低め、土曜は高めで、雨の日と晴れの日で差が出やすい。',
  gyouzinbashi: '近年はやや増加傾向。水曜は低め、土曜は高めで、イベント日の押し上げが大きい。',
  omotesando: '近年はやや増加傾向。木曜は低め、日曜は高めで、イベント日は通常日よりやや混みやすい。',
  yottekan: '全体は横ばい傾向。水曜は低め、土曜は高めで、イベント日に混雑が上がりやすい。',
  honmachi4: '近年はやや増加傾向。火曜は低め、土曜は高めで、イベント日や晴天日に混雑が乗りやすい。',
  honmachi3: '近年はやや増加傾向。水曜は低め、土曜は高めで、イベント日の押し上げが目立つ。',
  honmachi2: '近年はやや増加傾向。火曜は低め、土曜は高めで、イベント日の押し上げが目立つ。',
  kokubunjidori: '近年はやや増加傾向。火曜は低め、土曜は高めで、イベント日の増加が比較的はっきり出る。',
  yasukawadori: '近年はやや増加傾向。水曜は低め、日曜は高めで、イベント日に上振れしやすい。',
  jinnya: '全体は横ばい傾向。火曜は低め、日曜は高めで、イベント日は通常日より混みやすい。',
  nakabashi: '近年はやや増加傾向。水曜は低め、日曜は高めで、イベント日や晴天日に混雑が乗りやすい。',
};

/** @type {Record<string, string>} */
export const LOCATION_CONTEXT_EN = {
  'old-town': 'Recent trend is mildly upward. Wednesdays are usually lower, Saturdays higher, and event days tend to be busier than normal days.',
  station: 'Recent trend is mildly upward. Tuesdays are usually lower, Saturdays higher, and rainy vs sunny days often show a clear gap.',
  gyouzinbashi: 'Recent trend is mildly upward. Wednesdays are usually lower, Saturdays higher, and event-day lift is relatively large.',
  omotesando: 'Recent trend is mildly upward. Thursdays are usually lower, Sundays higher, and event days are somewhat busier than normal days.',
  yottekan: 'Overall trend is mostly flat. Wednesdays are usually lower, Saturdays higher, and congestion tends to rise on event days.',
  honmachi4: 'Recent trend is mildly upward. Tuesdays are usually lower, Saturdays higher, and congestion tends to rise on event days and sunny days.',
  honmachi3: 'Recent trend is mildly upward. Wednesdays are usually lower, Saturdays higher, and event-day lift is noticeable.',
  honmachi2: 'Recent trend is mildly upward. Tuesdays are usually lower, Saturdays higher, and event-day lift is noticeable.',
  kokubunjidori: 'Recent trend is mildly upward. Tuesdays are usually lower, Saturdays higher, and event-day increases are relatively clear.',
  yasukawadori: 'Recent trend is mildly upward. Wednesdays are usually lower, Sundays higher, and event days tend to show an uplift.',
  jinnya: 'Overall trend is mostly flat. Tuesdays are usually lower, Sundays higher, and event days are often busier than normal days.',
  nakabashi: 'Recent trend is mildly upward. Wednesdays are usually lower, Sundays higher, and congestion tends to rise on event days and sunny days.',
};

/** @type {Record<number, string>} month 1-12 */
export const MONTHLY_CONTEXT_JA = {
  1: '年間ではやや落ち着く月だが、正月前後は通常日より上がりやすい。',
  2: '全体は低めになりやすい。イベントが少ない月は天候で日ごとの差が出やすい。',
  3: '冬より持ち直しやすい。イベントの有無だけでなく、曜日と天候を合わせて見ると判断しやすい。',
  4: '混雑しやすい月。高山祭の押し上げが特に大きく、例外日が出やすい。',
  5: '混雑しやすい月。イベント日の上振れが目立ち、GW前後の運用判断が重要。',
  6: '中くらいからやや高め。雨の日でもイベント日は上がることがあり、天候だけで決めにくい。',
  7: '中くらいの月。イベント日が多い時期は、通常日との差が続きやすい。',
  8: '混雑しやすい月。イベント影響が強く、ピーク日のばらつきも大きくなりやすい。',
  9: '中くらいからやや高め。春やGWほどではないが、イベント日に上振れしやすい。',
  10: '年間でも混みやすい月。イベント日の押し上げがはっきり出やすい。',
  11: '混雑は高めだがイベントは少なめ。曜日と天候を主軸に見ると判断しやすい。',
  12: '全体は下がりやすい月。年末要因の変動はあるが、通常日は抑えめになりやすい。',
};

/** @type {Record<number, string>} */
export const MONTHLY_CONTEXT_EN = {
  1: 'Usually a bit calmer overall, but event days can still rise clearly above normal days.',
  2: 'Often one of the quieter months. When few events are listed, weather can drive day-to-day swings.',
  3: 'Usually recovers from winter. Read weekday and weather patterns together, not events alone.',
  4: 'Typically a busy month. Event-day lift can be large, so exception days matter more.',
  5: 'Typically a busy month. Event-day uplift is often clear, especially around Golden Week.',
  6: 'Usually medium to slightly high. Even rainy days can rise on event dates, so weather alone is not enough.',
  7: 'Usually around the middle. If event days are frequent, the gap vs normal days can persist.',
  8: 'Usually a busy month. Event impact is strong and peak-day variation can widen.',
  9: 'Usually medium to slightly high. Uplift exists, though often weaker than spring or Golden Week.',
  10: 'Often one of the busiest months. Event-day uplift is usually clear.',
  11: 'Congestion can stay relatively high even with fewer events; weekday and weather reads are important.',
  12: 'Overall levels often ease. Year-end effects can create spikes, but normal days are usually calmer.',
};

export function getLocationContext(locationKey, lang) {
  const map = lang === 'en' ? LOCATION_CONTEXT_EN : LOCATION_CONTEXT_JA;
  return map[locationKey] || (lang === 'en'
    ? 'Mixed local/tourism patterns may apply.'
    : '客層は地点により異なる（詳細は不明）。');
}

export function getMonthlyContext(monthNum, lang) {
  const m = Number(monthNum);
  const map = lang === 'en' ? MONTHLY_CONTEXT_EN : MONTHLY_CONTEXT_JA;
  return map[m] || '';
}

export function getTakayamaBase(lang) {
  return lang === 'en' ? TAKAYAMA_BASE_EN : TAKAYAMA_BASE_JA;
}
